// CanvasManagerAudit — Ship 11 v1 (Apr 30, 2026)
//
// Manager audit board. Six headline KPIs aligned to CRM phases (Knocks /
// Contacts / Apps / Claims / Builds / Revenue) over a date-range + hour-of-day
// filter. Plus a top-N storm dates table showing where Bardin should be
// working but maybe isn't (storm activity vs. our visit count).
//
// Manager-tier only — gated client-side; the RPCs are SECURITY DEFINER and
// the underlying tables are RLS-on, so a non-manager hitting the page won't
// see anything actionable anyway.
//
// v1 deliberately ships without per-zone drilldown / carrier breakdown /
// remove-keep flow — those need real canvas activity to be meaningful.
// Once we have a few weeks of real data we'll add v2.

import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";
import { useTabParam } from "../lib/useTabParam";
import OneOnOneMode from "../components/canvas/audit/OneOnOneMode";
import DailyBoard from "../components/canvas/audit/DailyBoard";
import CanvassersAuditMode from "../components/canvas/audit/CanvassersAuditMode";
import AreaMapStormDrawer from "../components/canvas/areas/AreaMapStormDrawer";
import AreasMode from "../components/canvas/areas/AreasMode";
import { fetchAreaStats } from "../lib/canvasAreas";

// Two Modes shell added Ship 2 of canvas-weekly-commitments (May 13, 2026):
//   📊 Team View   = existing audit content (KPIs + assigned areas + storm
//                    zones + shift activity)
//   🎯 1-on-1 Mode = new pane (OneOnOneMode) driven by weekly_commitments +
//                    weekly_commitment_match — left rail of reps + per-rep
//                    recap card. Editor + 30d toggle land in Ships 3-4.
//   📐 Areas       = today's daily areas, standing areas, week / month review
//                    (Oct 23, 2026)
const MODES = ["team", "today", "oneone", "canvassers", "areas"] as const;
type Mode = (typeof MODES)[number];

const MANAGER_ROLES = new Set([
  "admin","sales_director","asm","team_lead",
  "restore_manager","roof_team_manager","roof_director","director_of_operations",
]);

type RangeKey = "this_week" | "last_week" | "this_month" | "last_month" | "this_quarter" | "rolling_30" | "rolling_90";
const RANGE_LABELS: Record<RangeKey, string> = {
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  rolling_30: "Rolling 30d",
  rolling_90: "Rolling 90d",
};

type HourBand = "all" | "morning" | "afternoon" | "evening";
const HOUR_LABELS: Record<HourBand, string> = {
  all: "All day",
  morning: "9 AM–2 PM",
  afternoon: "2–6 PM",
  evening: "After 6 PM",
};

function rangeFor(key: RangeKey): { start: Date; end: Date } {
  const now = new Date();
  const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const endOfDay = (d: Date) => { const x = new Date(d); x.setHours(23,59,59,999); return x; };

  if (key === "rolling_30")    return { start: new Date(now.getTime() - 30 * 86400000), end: now };
  if (key === "rolling_90")    return { start: new Date(now.getTime() - 90 * 86400000), end: now };

  if (key === "this_week" || key === "last_week") {
    const d = new Date(now);
    const day = (d.getDay() + 6) % 7;        // Mon=0
    const monday = startOfDay(new Date(d.getTime() - day * 86400000));
    if (key === "this_week") return { start: monday, end: now };
    return { start: new Date(monday.getTime() - 7 * 86400000), end: new Date(monday.getTime() - 1) };
  }

  if (key === "this_month" || key === "last_month") {
    const d = new Date(now);
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    if (key === "this_month") return { start, end: now };
    const lastEnd = endOfDay(new Date(start.getTime() - 1));
    const lastStart = new Date(lastEnd.getFullYear(), lastEnd.getMonth(), 1);
    return { start: lastStart, end: lastEnd };
  }

  // this_quarter
  const q = Math.floor(now.getMonth() / 3);
  const start = new Date(now.getFullYear(), q * 3, 1);
  return { start, end: now };
}

function hourBand(b: HourBand): { hs: number | null; he: number | null } {
  switch (b) {
    case "morning":   return { hs: 9,  he: 14 };
    case "afternoon": return { hs: 14, he: 18 };
    case "evening":   return { hs: 18, he: 24 };
    default:          return { hs: null, he: null };
  }
}

interface Kpis {
  knocks: number;
  contacts: number;
  apps: number;
  apps_approved: number;
  claims: number;
  builds: number;
  revenue_cents: number;
}

interface StormDate {
  storm_date: string;
  signature_count: number;
  max_size_in: number;
  centroid_lat: number;
  centroid_lon: number;
  bardin_visits: number;
  bardin_appts: number;
  days_until_expire: number;
}

interface AuditSummary {
  active_areas: number;
  active_pins_assigned: number;
  expiring_30d_sigs: number;
  expiring_60d_sigs: number;
  open_pending_appointments: number;
}

export default function CanvasManagerAudit() {
  const { role, isSuperUser } = useAuth();
  const navigate = useNavigate();

  const [range, setRange] = useState<RangeKey>("this_month");
  const [band, setBand] = useState<HourBand>("all");
  const [kpis, setKpis] = useState<Kpis | null>(null);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [storms, setStorms] = useState<StormDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Now-tile click → filter the visible storm cards by days-until-expire
  const [expiringFilter, setExpiringFilter] = useState<0 | 30 | 60>(0);
  // One card can be expanded at a time
  const [expandedDate, setExpandedDate] = useState<string | null>(null);

  const isManager = useMemo(
    () => isSuperUser || (role && MANAGER_ROLES.has(role)),
    [role, isSuperUser]
  );

  // URL-backed mode toggle: ?mode=team (default) | ?mode=oneone
  const [mode, setMode] = useTabParam<Mode>("mode", "team", MODES);

  // Filter the storm cards by the Now-tile expiring filter (0 = no filter)
  const visibleStorms = useMemo(() => {
    if (expiringFilter === 0) return storms;
    return storms.filter((s) => s.days_until_expire > 0 && s.days_until_expire <= expiringFilter);
  }, [storms, expiringFilter]);

  // Bumps when a relevant table changes; triggers a refetch
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!isManager) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      const { start, end } = rangeFor(range);
      const { hs, he } = hourBand(band);
      const args = {
        p_start: start.toISOString(),
        p_end: end.toISOString(),
        p_hour_start: hs,
        p_hour_end: he,
      };
      const [kpisRes, stormsRes, summaryRes] = await Promise.all([
        supabase.rpc("get_canvas_audit_kpis", args),
        supabase.rpc("get_canvas_audit_storm_dates", { p_start: start.toISOString(), p_end: end.toISOString(), p_limit: 12 }),
        supabase.rpc("get_canvas_audit_summary"),
      ]);
      if (cancelled) return;
      if (kpisRes.error) setErr(`KPIs: ${kpisRes.error.message}`);
      else setKpis(kpisRes.data?.[0] ?? null);
      if (stormsRes.error) setErr((prev) => prev ?? `Storms: ${stormsRes.error!.message}`);
      else setStorms((stormsRes.data ?? []) as StormDate[]);
      if (summaryRes.error) setErr((prev) => prev ?? `Summary: ${summaryRes.error!.message}`);
      else setSummary((summaryRes.data?.[0] ?? null) as AuditSummary | null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [range, band, isManager, refreshTick]);

  // Realtime: bump refreshTick whenever a relevant canvas row changes. Debounced
  // by 1.5s so a burst of visits doesn't trigger 5 simultaneous refetches.
  useEffect(() => {
    if (!isManager) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    function bump() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setRefreshTick((n) => n + 1), 1500);
    }
    const channel = supabase
      .channel("canvas-audit-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "canvas_visits" }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "pending_canvas_appointments" }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "canvas_area_assignments" }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "knock_pins" }, bump)
      .subscribe();
    return () => {
      if (timer) clearTimeout(timer);
      supabase.removeChannel(channel);
    };
  }, [isManager]);

  if (!isManager) {
    return (
      <div className="mx-auto max-w-md p-6">
        <div className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          Manager-only audit board. Ask your manager for access.
        </div>
        <button onClick={() => navigate("/")} className="mt-3 rounded-md bg-slate-900 px-3 py-2 text-sm text-white">← Home</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="rounded-md px-2 py-1 text-sm font-semibold text-slate-700 hover:bg-slate-100">← Home</button>
            <div>
              <div className="text-base font-bold text-slate-900">Canvas Manager Audit</div>
              <div className="text-xs text-slate-500">
                {mode === "team"
                  ? "CRM-aligned KPIs + storm-zone activity"
                  : mode === "canvassers"
                  ? "Canvasser quality — hours, appts/hr, legit-app signals"
                  : mode === "today"
                  ? "Daily commitments — confirm each person's day by 10:00"
                  : mode === "areas"
                  ? "Canvas areas — today's, standing, and how they performed"
                  : "Monday 1-on-1 — weekly goals + the month's build goal"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/canvas")} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white">🗺 Canvas</button>
            <button onClick={() => navigate("/canvas/approvals")} className="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white">Approvals</button>
          </div>
        </div>
        {/* Two Modes toggle */}
        <div className="mx-auto max-w-6xl overflow-x-auto px-4 pb-3">
          <div className="inline-flex gap-0.5 whitespace-nowrap rounded-lg bg-slate-900 p-1">
            <button
              onClick={() => setMode("team")}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${mode === "team" ? "bg-orange-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              📊 Team View
            </button>
            <button
              onClick={() => setMode("today")}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${mode === "today" ? "bg-orange-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              ✅ Today
            </button>
            <button
              onClick={() => setMode("oneone")}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${mode === "oneone" ? "bg-orange-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              🎯 1-on-1 Mode
            </button>
            <button
              onClick={() => setMode("canvassers")}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${mode === "canvassers" ? "bg-orange-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              🚪 Canvassers
            </button>
            <button
              onClick={() => setMode("areas")}
              className={`rounded-md px-3 py-1.5 text-xs font-bold transition ${mode === "areas" ? "bg-orange-500 text-white" : "text-white/60 hover:text-white"}`}
            >
              📐 Areas
            </button>
          </div>
        </div>
      </div>

      {mode === "areas" ? (
        <div className="mx-auto max-w-6xl space-y-5 p-4">
          <AreasMode />
        </div>
      ) : mode === "canvassers" ? (
        <div className="mx-auto max-w-6xl space-y-5 p-4">
          <CanvassersAuditMode />
        </div>
      ) : mode === "today" ? (
        <div className="mx-auto max-w-6xl space-y-5 p-4">
          <DailyBoard />
        </div>
      ) : mode === "oneone" ? (
        <div className="mx-auto max-w-6xl space-y-5 p-4">
          <OneOnOneMode />
        </div>
      ) : (
      <div className="mx-auto max-w-6xl space-y-5 p-4">
        {/* Filter bar */}
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Date range</span>
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((k) => (
              <Pill key={k} active={range === k} onClick={() => setRange(k)}>{RANGE_LABELS[k]}</Pill>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hour of day</span>
            {(Object.keys(HOUR_LABELS) as HourBand[]).map((k) => (
              <Pill key={k} active={band === k} onClick={() => setBand(k)}>{HOUR_LABELS[k]}</Pill>
            ))}
          </div>
        </div>

        {err ? (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-800">⚠ {err}</div>
        ) : null}

        {/* Org-wide summary strip — independent of date filter */}
        {summary ? (
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Now</div>
              <div className="text-[10px] text-slate-400">org-wide · live</div>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              <SummaryTile
                label="Active areas"
                value={summary.active_areas}
                hint={`${summary.active_pins_assigned.toLocaleString()} pins under assignment`}
                accent="orange"
                onClick={() => setMode("areas")}
              />
              <SummaryTile
                label="≤30d expiring"
                value={summary.expiring_30d_sigs}
                hint="≥1″ sigs · file claims now"
                accent="red"
                urgent
                active={expiringFilter === 30}
                onClick={() => setExpiringFilter((v) => v === 30 ? 0 : 30)}
              />
              <SummaryTile
                label="≤60d expiring"
                value={summary.expiring_60d_sigs}
                hint="≥1″ sigs · approaching deadline"
                accent="red"
                active={expiringFilter === 60}
                onClick={() => setExpiringFilter((v) => v === 60 ? 0 : 60)}
              />
              <SummaryTile
                label="Pending approvals"
                value={summary.open_pending_appointments}
                hint="canvasser-submitted appts"
                accent="orange"
                onClick={() => navigate("/canvas/approvals")}
              />
              <SummaryTile
                label="Pins covered"
                value={summary.active_pins_assigned}
                hint="across all active areas"
                accent="teal"
                onClick={() => navigate("/canvas")}
              />
            </div>
            {expiringFilter > 0 ? (
              <div className="mt-3 flex items-center gap-2 rounded-md bg-red-50 px-3 py-1.5 text-xs">
                <span className="font-semibold text-red-800">🚨 Filtering storm cards to ≤{expiringFilter}d to file</span>
                <button
                  onClick={() => setExpiringFilter(0)}
                  className="ml-auto rounded bg-white px-2 py-0.5 text-[10px] font-bold text-red-700 hover:bg-red-100"
                >
                  Clear filter
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* KPI tiles */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <Kpi label="Knocks" value={kpis?.knocks ?? 0} hint="canvas_visits" loading={loading} accent="orange" />
          <Kpi label="Contacts" value={kpis?.contacts ?? 0} hint="contact_made" loading={loading} accent="purple" />
          <Kpi label="Apps" value={kpis?.apps ?? 0} hint={`${kpis?.apps_approved ?? 0} approved`} loading={loading} accent="peach" />
          <Kpi label="Claims" value={kpis?.claims ?? 0} hint="canvas-sourced" loading={loading} accent="cyan" />
          <Kpi label="Builds" value={kpis?.builds ?? 0} hint="completed in window" loading={loading} accent="green" />
          <Kpi label="Revenue" value={fmtMoney(kpis?.revenue_cents ?? 0)} hint="commissionable" loading={loading} accent="teal" />
        </div>

        {/* All assigned areas (standalone management surface — independent of storm) */}
        <AssignedAreasSection />

        {/* Storm zone cards (expandable) */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Top active storm dates · metro Atlanta · {visibleStorms.length}{" "}
                {expiringFilter > 0 ? `of ${storms.length}` : ""} shown
              </div>
              <div className="text-[11px] text-slate-400">
                ≥1″ hail within 75 mi of Marietta · tap a card to drill into assigned and unassigned sub-areas.
              </div>
            </div>
          </div>
          {loading ? (
            <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-500">Loading…</div>
          ) : visibleStorms.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
              {expiringFilter > 0 ? `No storms expiring in ≤${expiringFilter} days.` : "No storm data in window yet."}
            </div>
          ) : (
            <div className="space-y-3">
              {visibleStorms.map((s) => (
                <StormZoneCard
                  key={s.storm_date}
                  storm={s}
                  rangeStart={rangeFor(range).start}
                  rangeEnd={rangeFor(range).end}
                  expanded={expandedDate === s.storm_date}
                  onToggle={() => setExpandedDate((cur) => cur === s.storm_date ? null : s.storm_date)}
                />
              ))}
            </div>
          )}
        </div>

        <ShiftActivityCard rangeStart={rangeFor(range).start} rangeEnd={rangeFor(range).end} />

        <div className="text-xs text-slate-500">
          Carrier breakdown is stubbed for now — needs canvas-sourced deals to populate. Will go live once Ship 8 starts producing them.
        </div>
      </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// AssignedAreasSection — standalone "all assigned areas" management surface.
// Independent of the storm-date drilldown above. Lists every area
// (active + removed in the last 30d), grouped by area_group_id so all reps
// sharing a boundary collapse to one row with chips. Lets managers:
//   • Manage reps on an area (reuses ManageRepsModal)
//   • Mark the area reviewed (clears the 7d stale flag)
//   • Remove the area (returns pins to the Unassigned pool)
//   • Filter by storm-tied / freestyle, search by rep or label,
//     toggle removed visibility.
// All actions are direct table writes — manager RLS covers them (per the
// canvas_can_manage() policy on canvas_area_assignments).
// ─────────────────────────────────────────────────────────────────────────

interface AreaAssignmentRow {
  id: string;
  area_group_id: string;
  assigned_to: string;
  assigned_by: string | null;
  area_label: string | null;
  notes: string | null;
  pin_ids: string[] | null;
  share_mode: string | null;
  storm_signature_id: string | null;
  status: string;
  assigned_at: string;
  removed_at: string | null;
  last_reviewed_at: string | null;
  for_date: string | null;
  team_roster: { full_name: string | null; role: string | null } | null;
}

interface AreaGroup {
  area_group_id: string;
  area_label: string | null;
  pin_ids: string[];
  pin_count: number;
  storm_signature_id: string | null;
  share_mode: string | null;
  notes: string | null;
  status: "active" | "removed";
  first_assigned_at: string;
  last_reviewed_at: string | null;
  for_date: string | null;
  reps: Array<{
    row_id: string;
    user_id: string;
    full_name: string;
    role: string;
    status: string;
    removed_at: string | null;
  }>;
}

function AssignedAreasSection() {
  const [rows, setRows] = useState<AreaAssignmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);
  const [search, setSearch] = useState("");
  const [stormFilter, setStormFilter] = useState<"all" | "storm" | "freestyle">("all");
  const [manageRow, setManageRow] = useState<ZoneDetailRow | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [busyGroupId, setBusyGroupId] = useState<string | null>(null);
  // Inline map + storm drawer: which area_group_id is expanded, and the
  // boundary polygon for each active group (keyed by area_group_id). Geometry
  // comes from get_active_canvas_areas_geojson() — the same RPC the /canvas map
  // uses — so the outline here matches what reps see in the field.
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);
  const [geomByGroup, setGeomByGroup] = useState<Map<string, any>>(new Map());
  // Live pin count per active area (pins inside the polygon now — the same
  // number the Today tab and the rep's home widget show). The stored pin_ids
  // is a snapshot from when the lasso was drawn and drifts (Oct 19, 2026:
  // Acworth 615 stored vs 448 live). Removed areas keep the snapshot.
  const [livePins, setLivePins] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    let cancelled = false;
    fetchAreaStats()
      .then((rows) => { if (!cancelled) setLivePins(new Map(rows.map((a) => [a.area_group_id, a.pin_count]))); })
      .catch((e) => console.warn("canvas_area_stats:", (e as Error).message));
    return () => { cancelled = true; };
  }, [refreshTick]);
  const pinsFor = (g: { area_group_id: string; pin_count: number; status: string }) =>
    g.status === "active" ? (livePins.get(g.area_group_id) ?? g.pin_count) : g.pin_count;

  // Pull last 30d of activity. Removed rows older than that drop off the list
  // (still in DB, just not visible here — keeps the table scannable). If
  // anyone needs deeper history we can promote to "all" via a future toggle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86_400_000).toISOString();
      // Two queries (active OR recently-removed) — simpler than juggling a
      // PostgREST `.or()` clause with comma-separated nested predicates.
      const [activeRes, removedRes] = await Promise.all([
        supabase
          .from("canvas_area_assignments")
          .select(
            "id, area_group_id, assigned_to, assigned_by, area_label, notes, pin_ids, share_mode, storm_signature_id, status, assigned_at, removed_at, last_reviewed_at, for_date, team_roster!canvas_area_assignments_assigned_to_fkey(full_name, role)"
          )
          .eq("status", "active")
          .order("assigned_at", { ascending: false })
          .limit(2000),
        supabase
          .from("canvas_area_assignments")
          .select(
            "id, area_group_id, assigned_to, assigned_by, area_label, notes, pin_ids, share_mode, storm_signature_id, status, assigned_at, removed_at, last_reviewed_at, for_date, team_roster!canvas_area_assignments_assigned_to_fkey(full_name, role)"
          )
          .eq("status", "removed")
          .gte("removed_at", thirtyDaysAgo)
          .order("removed_at", { ascending: false })
          .limit(500),
      ]);
      if (cancelled) return;
      if (activeRes.error) setErr(activeRes.error.message);
      else if (removedRes.error) setErr(removedRes.error.message);
      else {
        // PostgREST's TS types sometimes infer the FK embed as an array even
        // when the actual response is a single object. Normalize both shapes
        // so downstream code can do `row.team_roster?.full_name`.
        const normalize = (raw: any[]): AreaAssignmentRow[] =>
          raw.map((r) => ({
            ...r,
            team_roster: Array.isArray(r.team_roster)
              ? (r.team_roster[0] ?? null)
              : r.team_roster,
          }));
        setRows([...normalize(activeRes.data ?? []), ...normalize(removedRes.data ?? [])]);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  // Realtime — refetch on any canvas_area_assignments change.
  useEffect(() => {
    const ch = supabase
      .channel("assigned-areas-section")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "canvas_area_assignments" },
        () => setRefreshTick((n) => n + 1)
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // Pull active-area boundary polygons (one feature per assignment row; reps
  // sharing an area carry the same boundary). Build a map keyed by
  // area_group_id so each row can render its outline + locate storms. Refetched
  // on the same tick as the table so a freshly-assigned area gets a geometry.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_active_canvas_areas_geojson");
      if (cancelled) return;
      if (error) { console.warn("get_active_canvas_areas_geojson:", error.message); return; }
      const m = new Map<string, any>();
      for (const f of (data?.features ?? [])) {
        const gid = f?.properties?.area_group_id;
        if (gid && f.geometry && !m.has(gid)) m.set(gid, f.geometry);
      }
      setGeomByGroup(m);
    })();
    return () => { cancelled = true; };
  }, [refreshTick]);

  // Collapse the (rep × area) rows into one row per area_group_id.
  const groups = useMemo<AreaGroup[]>(() => {
    const map = new Map<string, AreaGroup>();
    for (const r of rows) {
      let g = map.get(r.area_group_id);
      if (!g) {
        g = {
          area_group_id: r.area_group_id,
          area_label: r.area_label,
          pin_ids: r.pin_ids ?? [],
          pin_count: (r.pin_ids ?? []).length,
          storm_signature_id: r.storm_signature_id,
          share_mode: r.share_mode,
          notes: r.notes,
          status: r.status === "active" ? "active" : "removed",
          first_assigned_at: r.assigned_at,
          last_reviewed_at: r.last_reviewed_at,
          for_date: r.for_date ?? null,
          reps: [],
        };
        map.set(r.area_group_id, g);
      } else {
        // Fold in: any active rep flips the group to active; earliest
        // assigned_at + latest last_reviewed_at win; bigger pin_ids array wins
        // (different reps' rows can briefly drift if anyone hand-edited).
        if (r.status === "active") g.status = "active";
        if (r.assigned_at < g.first_assigned_at) g.first_assigned_at = r.assigned_at;
        if (r.last_reviewed_at && (!g.last_reviewed_at || r.last_reviewed_at > g.last_reviewed_at)) {
          g.last_reviewed_at = r.last_reviewed_at;
        }
        if ((r.pin_ids?.length ?? 0) > g.pin_count) {
          g.pin_ids = r.pin_ids ?? [];
          g.pin_count = r.pin_ids?.length ?? 0;
        }
        if (!g.area_label && r.area_label) g.area_label = r.area_label;
        if (!g.notes && r.notes) g.notes = r.notes;
        if (!g.for_date && r.for_date) g.for_date = r.for_date;
      }
      g.reps.push({
        row_id: r.id,
        user_id: r.assigned_to,
        full_name: r.team_roster?.full_name ?? "—",
        role: r.team_roster?.role ?? "",
        status: r.status,
        removed_at: r.removed_at,
      });
    }
    // Sort reps within a group: actives first by name, then removed by name.
    for (const g of map.values()) {
      g.reps.sort((a, b) => {
        if (a.status !== b.status) return a.status === "active" ? -1 : 1;
        return a.full_name.localeCompare(b.full_name);
      });
    }
    return Array.from(map.values()).sort((a, b) =>
      b.first_assigned_at.localeCompare(a.first_assigned_at)
    );
  }, [rows]);

  const filtered = useMemo(() => {
    let xs = groups;
    if (!showRemoved) xs = xs.filter((g) => g.status === "active");
    if (stormFilter === "storm")     xs = xs.filter((g) => !!g.storm_signature_id);
    if (stormFilter === "freestyle") xs = xs.filter((g) => !g.storm_signature_id);
    const q = search.trim().toLowerCase();
    if (q) {
      xs = xs.filter((g) =>
        (g.area_label?.toLowerCase().includes(q) ?? false) ||
        g.reps.some((r) => r.full_name.toLowerCase().includes(q))
      );
    }
    return xs;
  }, [groups, showRemoved, stormFilter, search]);

  const visiblePinTotal = useMemo(
    () => filtered.reduce((n, g) => n + (g.status === "active" ? (livePins.get(g.area_group_id) ?? g.pin_count) : 0), 0),
    [filtered, livePins]
  );

  async function removeArea(groupId: string) {
    if (!confirm("Remove this area? All assigned reps will be unassigned and pins return to the Unassigned pool.")) return;
    setErr(null);
    setBusyGroupId(groupId);
    const { error } = await supabase
      .from("canvas_area_assignments")
      .update({ status: "removed", removed_at: new Date().toISOString() })
      .eq("area_group_id", groupId)
      .eq("status", "active");
    setBusyGroupId(null);
    if (error) { setErr(error.message); return; }
    setRefreshTick((n) => n + 1);
  }

  // Bulk-stamp all active rows in the group. RLS lets managers update directly
  // (canvas_can_manage() policy). Doing this via UPDATE instead of looping the
  // canvas_assignment_mark_reviewed RPC keeps it to a single round-trip.
  async function markReviewed(groupId: string) {
    setErr(null);
    setBusyGroupId(groupId);
    const { error } = await supabase
      .from("canvas_area_assignments")
      .update({ last_reviewed_at: new Date().toISOString() })
      .eq("area_group_id", groupId)
      .eq("status", "active");
    setBusyGroupId(null);
    if (error) { setErr(error.message); return; }
    setRefreshTick((n) => n + 1);
  }

  function openManage(g: AreaGroup) {
    // ManageRepsModal only reads area_group_id + area_label + pin_count from
    // the row it's handed. We construct a synthetic ZoneDetailRow so we don't
    // need to refactor the modal — the storm-card path uses the same shape.
    setManageRow({
      row_kind: "area",
      area_id: g.reps.find((r) => r.status === "active")?.row_id ?? null,
      area_group_id: g.area_group_id,
      assigned_to: g.reps.find((r) => r.status === "active")?.user_id ?? null,
      assigned_to_name: g.reps.find((r) => r.status === "active")?.full_name ?? null,
      area_label: g.area_label,
      assigned_at: g.first_assigned_at,
      pin_count: pinsFor(g),
      knocks: 0, contacts: 0, apps: 0, claims: 0, builds: 0, revenue_cents: 0,
      last_activity_at: null,
      last_reviewed_at: g.last_reviewed_at,
    });
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assigned areas</div>
          <div className="text-[11px] text-slate-400">
            {filtered.length} {filtered.length === 1 ? "area" : "areas"} ·{" "}
            {visiblePinTotal.toLocaleString()} pins under assignment
            {showRemoved ? " · including removed (last 30d)" : ""}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rep or label…"
            className="w-40 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs"
          />
          <select
            value={stormFilter}
            onChange={(e) => setStormFilter(e.target.value as "all" | "storm" | "freestyle")}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700"
            title="Filter by area type"
          >
            <option value="all">All areas</option>
            <option value="storm">Storm-tied</option>
            <option value="freestyle">Freestyle</option>
          </select>
          <button
            onClick={() => setShowRemoved((v) => !v)}
            className={`rounded-md px-2 py-1 text-[10px] font-semibold ${
              showRemoved ? "bg-slate-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            title="Toggle removed areas (last 30d)"
          >
            {showRemoved ? "Hide removed" : "Show removed"}
          </button>
        </div>
      </div>

      {err ? (
        <div className="mb-2 rounded-md bg-red-50 p-2 text-xs text-red-800">⚠ {err}</div>
      ) : null}

      {loading ? (
        <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">Loading assigned areas…</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-md bg-slate-50 p-4 text-center text-xs text-slate-500">
          {rows.length === 0
            ? "No areas have been assigned yet. Lasso reps on /canvas to create one."
            : "No areas match the current filters."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="py-1.5 text-left">Area</th>
                <th className="py-1.5 text-left">Reps</th>
                <th className="py-1.5 text-right">Pins</th>
                <th className="py-1.5 text-left whitespace-nowrap">Assigned</th>
                <th className="py-1.5 text-left whitespace-nowrap">Reviewed</th>
                <th className="py-1.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => {
                const reviewedTime = g.last_reviewed_at ? new Date(g.last_reviewed_at).getTime() : null;
                const assignedTime = new Date(g.first_assigned_at).getTime();
                const idleSrc = reviewedTime ?? assignedTime;
                const idleDays = Math.floor((Date.now() - idleSrc) / 86_400_000);
                const isStale = g.status === "active" && idleDays >= 7;
                const isRemoved = g.status === "removed";
                const busy = busyGroupId === g.area_group_id;
                const geom = geomByGroup.get(g.area_group_id);
                const isExpanded = expandedGroupId === g.area_group_id;
                return (
                  <Fragment key={g.area_group_id}>
                  <tr
                    className={`border-b border-slate-100 last:border-0 ${
                      isRemoved ? "bg-slate-50 text-slate-500" : "hover:bg-slate-50"
                    }`}
                  >
                    <td className="py-2 pr-2">
                      <div className={`font-semibold ${isRemoved ? "text-slate-500 line-through" : "text-slate-900"}`}>
                        {g.area_label?.trim() || <span className="italic text-slate-400">(unlabeled)</span>}
                      </div>
                      {g.for_date ? (
                        <div className="text-[10px] font-bold text-sky-700">
                          Daily · {new Date(g.for_date + "T12:00:00Z").toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" })}
                        </div>
                      ) : null}
                      <div className="text-[10px] text-slate-500">
                        {g.storm_signature_id ? (
                          <span className="rounded bg-orange-50 px-1 py-0.5 font-semibold text-orange-700">⛈ Storm-tied</span>
                        ) : (
                          <span className="rounded bg-slate-100 px-1 py-0.5 font-semibold text-slate-600">Freestyle</span>
                        )}
                        {g.share_mode ? <span className="ml-1">· {g.share_mode}</span> : null}
                        {g.notes ? <span className="ml-1" title={g.notes}>· 📝</span> : null}
                      </div>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="flex flex-wrap gap-1">
                        {g.reps.filter((r) => r.status === "active").map((r) => (
                          <span
                            key={r.row_id}
                            title={r.role}
                            className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold text-orange-900"
                          >
                            {r.full_name}
                          </span>
                        ))}
                        {showRemoved
                          ? g.reps.filter((r) => r.status === "removed").map((r) => (
                              <span
                                key={r.row_id}
                                title={`removed${r.removed_at ? ` ${relativeTime(r.removed_at)}` : ""}`}
                                className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500 line-through"
                              >
                                {r.full_name}
                              </span>
                            ))
                          : null}
                        {g.reps.filter((r) => r.status === "active").length === 0 ? (
                          <span className="text-[10px] italic text-slate-400">no active reps</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="py-2 pr-2 text-right font-bold tabular-nums"
                      title={g.status === "active" && livePins.has(g.area_group_id)
                        ? `Pins inside the area now (${g.pin_count} when it was drawn)` : "Pins when the area was drawn"}>
                      {pinsFor(g)}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap text-slate-700">
                      {relativeTime(g.first_assigned_at)}
                    </td>
                    <td className="py-2 pr-2 whitespace-nowrap text-slate-700">
                      {g.last_reviewed_at
                        ? relativeTime(g.last_reviewed_at)
                        : <span className="text-slate-300">never</span>}
                      {isStale ? (
                        <span
                          className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800"
                          title={`${idleDays} days since last review or assignment`}
                        >
                          Stale · {idleDays}d
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 text-right">
                      {isRemoved ? (
                        <span className="text-[10px] text-slate-500">Removed</span>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setExpandedGroupId(isExpanded ? null : g.area_group_id)}
                            disabled={!geom}
                            className={`rounded border px-2 py-0.5 text-[10px] font-semibold disabled:opacity-40 ${
                              isExpanded
                                ? "border-orange-400 bg-orange-50 text-orange-700"
                                : "border-slate-300 text-slate-700 hover:bg-slate-100"
                            }`}
                            title={geom ? "Show this area on the map + storms/hail in it" : "No boundary on file for this area"}
                          >
                            {isExpanded ? "Hide map ▲" : "🗺 Map ▾"}
                          </button>
                          <button
                            onClick={() => openManage(g)}
                            disabled={busy}
                            className="rounded border border-slate-300 px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                            title="Add or remove reps from this area"
                          >
                            Manage
                          </button>
                          <button
                            onClick={() => markReviewed(g.area_group_id)}
                            disabled={busy}
                            className="rounded border border-emerald-300 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                            title="Stamp the area as reviewed — clears the stale flag for 7 days"
                          >
                            Mark reviewed
                          </button>
                          <button
                            onClick={() => removeArea(g.area_group_id)}
                            disabled={busy}
                            className="rounded border border-red-300 px-2 py-0.5 text-[10px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                            title="Unassign all reps and return pins to the Unassigned pool"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && geom ? (
                    <tr>
                      <td colSpan={6} className="bg-slate-50 px-2 pb-3 pt-1">
                        <AreaMapStormDrawer geometry={geom} />
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {manageRow ? (
        <ManageRepsModal
          row={manageRow}
          onClose={() => setManageRow(null)}
          onSaved={async () => { setManageRow(null); setRefreshTick((n) => n + 1); }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ShiftActivityCard — Ship 6 v2. Per-shift table for the date range so
// managers can scan: who worked when, knocks/contacts/appts/miles, and
// flags for low conversion / short session / few pings. Click a row to
// jump to a per-rep deep-dive (deferred — TODO route to /admin/canvas-activity/{user_id}).
// ─────────────────────────────────────────────────────────────────────────

interface ShiftRow {
  shift_id: string;
  user_id: string;
  full_name: string;
  role: string;
  started_at: string;
  ended_at: string | null;
  hours_worked: number;
  knocks: number;
  contacts: number;
  appts: number;
  claims: number;
  miles_walked: number | null;
  pings: number;
  contact_rate: number;
  appt_rate: number;
  has_low_contact: boolean;
  has_low_appt: boolean;
  has_short_session: boolean;
  has_few_pings: boolean;
}

type ShiftSort = "newest" | "knocks" | "contacts" | "appts" | "claims" | "miles" | "rate";

function ShiftActivityCard({ rangeStart, rangeEnd }: { rangeStart: Date; rangeEnd: Date }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ShiftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sort, setSort] = useState<ShiftSort>("newest");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);

  function openRepDay(r: ShiftRow) {
    // Drill into per-rep audit for the day this shift started
    const d = new Date(r.started_at);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    navigate(`/admin/canvas-activity/${r.user_id}?date=${yyyy}-${mm}-${dd}`);
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      const { data, error } = await supabase.rpc("get_canvas_audit_shifts", {
        p_start: rangeStart.toISOString(),
        p_end: rangeEnd.toISOString(),
        p_user: null,
      });
      if (cancelled) return;
      if (error) setErr(error.message);
      else setRows((data ?? []) as ShiftRow[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [rangeStart.getTime(), rangeEnd.getTime()]);

  const visible = useMemo(() => {
    let xs = rows;
    if (showFlaggedOnly) {
      xs = xs.filter((r) => r.has_low_contact || r.has_low_appt || r.has_short_session || r.has_few_pings);
    }
    const cmp: Record<ShiftSort, (a: ShiftRow, b: ShiftRow) => number> = {
      newest:   (a, b) => b.started_at.localeCompare(a.started_at),
      knocks:   (a, b) => b.knocks - a.knocks,
      contacts: (a, b) => b.contacts - a.contacts,
      appts:    (a, b) => b.appts - a.appts,
      claims:   (a, b) => (b.claims ?? 0) - (a.claims ?? 0),
      miles:    (a, b) => (b.miles_walked ?? 0) - (a.miles_walked ?? 0),
      rate:     (a, b) => b.contact_rate - a.contact_rate,
    };
    return [...xs].sort(cmp[sort]);
  }, [rows, sort, showFlaggedOnly]);

  // Aggregate strip across the visible rows so managers see range totals
  const totals = useMemo(() => {
    let knocks = 0, contacts = 0, appts = 0, claims = 0, miles = 0, hrs = 0, shifts = 0;
    for (const r of visible) {
      knocks += r.knocks; contacts += r.contacts; appts += r.appts; claims += r.claims ?? 0;
      miles += Number(r.miles_walked ?? 0); hrs += Number(r.hours_worked); shifts++;
    }
    return { knocks, contacts, appts, claims, miles, hrs, shifts };
  }, [visible]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Shift activity</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFlaggedOnly((v) => !v)}
            className={`rounded-md px-2 py-1 text-[10px] font-semibold ${
              showFlaggedOnly ? "bg-amber-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            title="Show only shifts with coachable flags"
          >
            ⚑ Flagged only
          </button>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as ShiftSort)}
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[10px] font-semibold text-slate-700"
          >
            <option value="newest">Sort: Newest</option>
            <option value="knocks">Sort: Knocks</option>
            <option value="contacts">Sort: Contacts</option>
            <option value="appts">Sort: Appts</option>
            <option value="claims">Sort: Claims</option>
            <option value="miles">Sort: Miles</option>
            <option value="rate">Sort: Contact rate</option>
          </select>
        </div>
      </div>

      {/* Range totals */}
      <div className="mb-3 grid grid-cols-4 gap-2 sm:grid-cols-7">
        <ShiftAggTile label="Shifts"   value={totals.shifts} />
        <ShiftAggTile label="Hours"    value={totals.hrs.toFixed(1)} />
        <ShiftAggTile label="Knocks"   value={totals.knocks} />
        <ShiftAggTile label="Contacts" value={totals.contacts} />
        <ShiftAggTile label="Appts"    value={totals.appts} />
        <ShiftAggTile label="Claims"   value={totals.claims} />
        <ShiftAggTile label="Miles"    value={totals.miles.toFixed(1)} />
      </div>

      {err ? (
        <div className="rounded-md bg-red-50 p-2 text-xs text-red-800">⚠ {err}</div>
      ) : loading ? (
        <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">Loading shifts…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-500">
          {rows.length === 0 ? "No shifts in this date range yet." : "No shifts match the current filter."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="py-1.5 text-left">Rep</th>
                <th className="py-1.5 text-left">Started</th>
                <th className="py-1.5 text-right">Hrs</th>
                <th className="py-1.5 text-right">K</th>
                <th className="py-1.5 text-right">C</th>
                <th className="py-1.5 text-right">A</th>
                <th className="py-1.5 text-right" title="Claims credited to this shift">Cl</th>
                <th className="py-1.5 text-right">Mi</th>
                <th className="py-1.5 text-right">K→C</th>
                <th className="py-1.5 text-right">C→A</th>
                <th className="py-1.5 text-left">Flags</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr
                  key={r.shift_id}
                  onClick={() => openRepDay(r)}
                  className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50"
                  title="View this rep's day in detail"
                >
                  <td className="py-1.5">
                    <div className="font-semibold text-slate-900">{r.full_name}</div>
                    <div className="text-[10px] text-slate-500">{r.role}</div>
                  </td>
                  <td className="py-1.5 whitespace-nowrap text-slate-700">
                    {new Date(r.started_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    {r.ended_at == null ? <span className="ml-1 rounded bg-emerald-100 px-1 py-0.5 text-[9px] font-bold text-emerald-700">LIVE</span> : null}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{Number(r.hours_worked).toFixed(1)}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{r.knocks}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{r.contacts}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold">{r.appts}</td>
                  <td className="py-1.5 text-right tabular-nums font-semibold text-cyan-700">{r.claims ?? 0}</td>
                  <td className="py-1.5 text-right tabular-nums">{r.miles_walked != null ? Number(r.miles_walked).toFixed(1) : "—"}</td>
                  <td className="py-1.5 text-right tabular-nums">{(r.contact_rate * 100).toFixed(0)}%</td>
                  <td className="py-1.5 text-right tabular-nums">{(r.appt_rate * 100).toFixed(0)}%</td>
                  <td className="py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {r.has_low_contact ? <FlagPill color="amber" title="<25% knock-to-contact">low K→C</FlagPill> : null}
                      {r.has_low_appt    ? <FlagPill color="amber" title="<20% contact-to-appt">low C→A</FlagPill> : null}
                      {r.has_short_session ? <FlagPill color="slate" title="Less than 30 min on shift">short</FlagPill> : null}
                      {r.has_few_pings   ? <FlagPill color="red" title="Pings/hour below 6 — phone may have been backgrounded">few pings</FlagPill> : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ShiftAggTile({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border border-slate-100 bg-slate-50 p-2 text-center">
      <div className="text-base font-bold leading-none tabular-nums text-slate-900">{value}</div>
      <div className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}

function FlagPill({ color, title, children }: { color: "amber" | "red" | "slate"; title: string; children: React.ReactNode }) {
  const cls =
    color === "red"   ? "bg-red-100 text-red-800" :
    color === "amber" ? "bg-amber-100 text-amber-800" :
    "bg-slate-100 text-slate-700";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold ${cls}`} title={title}>{children}</span>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// StormZoneCard — expandable card for a single storm date. Header shows zone
// metadata + 6 zone-level KPIs. Body (when expanded) shows sub-area drilldown:
// the unassigned pool first, then one row per active area whose centroid is in
// the 5-mi zone, with rep avatars + per-area KPIs + Remove/Keep/Manage Reps.
// Carrier breakdown is stubbed until canvas-sourced deals exist.
// ─────────────────────────────────────────────────────────────────────────

interface ZoneDetailRow {
  row_kind: "zone" | "unassigned" | "area";
  area_id: string | null;
  area_group_id: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  area_label: string | null;
  assigned_at: string | null;
  pin_count: number;
  knocks: number;
  contacts: number;
  apps: number;
  claims: number;
  builds: number;
  revenue_cents: number;
  last_activity_at: string | null;
  last_reviewed_at: string | null;
}

// Days since the most recent activity OR manager review on this area.
// Returns null when both are missing AND the assignment was made <7 days ago
// (so brand-new assignments don't immediately show "stale"). Otherwise returns
// floor(days_since(max(last_activity, last_reviewed, assigned_at))) — whichever
// is most recent.
function daysIdleFor(r: ZoneDetailRow): number | null {
  const candidates: number[] = [];
  if (r.last_activity_at) candidates.push(new Date(r.last_activity_at).getTime());
  if (r.last_reviewed_at) candidates.push(new Date(r.last_reviewed_at).getTime());
  if (r.assigned_at)      candidates.push(new Date(r.assigned_at).getTime());
  if (candidates.length === 0) return null;
  const ms = Date.now() - Math.max(...candidates);
  return Math.floor(ms / 86_400_000);
}

function StormZoneCard({
  storm, rangeStart, rangeEnd, expanded, onToggle,
}: {
  storm: StormDate;
  rangeStart: Date;
  rangeEnd: Date;
  expanded: boolean;
  onToggle: () => void;
}) {
  const [detail, setDetail] = useState<ZoneDetailRow[] | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [manageRow, setManageRow] = useState<ZoneDetailRow | null>(null);
  const [carriers, setCarriers] = useState<Array<{
    carrier: string; total_deals: number; door_stated: number; corrected_to: number;
    claims_filed: number; claims_approved: number; builds_completed: number;
    total_corrections: number;
  }>>([]);

  async function loadDetail() {
    setLoadingDetail(true);
    setActionErr(null);
    const [zoneRes, carrierRes] = await Promise.all([
      supabase.rpc("get_storm_zone_detail", {
        p_storm_date: storm.storm_date,
        p_zone_lat: storm.centroid_lat,
        p_zone_lon: storm.centroid_lon,
        p_zone_radius_mi: 5,
        p_start: rangeStart.toISOString(),
        p_end: rangeEnd.toISOString(),
      }),
      supabase.rpc("get_storm_zone_carrier_breakdown", {
        p_zone_lat: storm.centroid_lat,
        p_zone_lon: storm.centroid_lon,
        p_zone_radius_mi: 5,
      }),
    ]);
    if (zoneRes.error) setActionErr(zoneRes.error.message);
    setDetail((zoneRes.data ?? []) as ZoneDetailRow[]);
    if (!carrierRes.error) setCarriers((carrierRes.data ?? []) as any);
    setLoadingDetail(false);
  }

  useEffect(() => {
    if (expanded && !detail) loadDetail();
  }, [expanded]);

  async function removeArea(areaGroupId: string) {
    if (!confirm("Remove this assigned area? Pins return to the Unassigned pool.")) return;
    setActionErr(null);
    const { error } = await supabase
      .from("canvas_area_assignments")
      .update({ status: "removed", removed_at: new Date().toISOString() })
      .eq("area_group_id", areaGroupId)
      .eq("status", "active");
    if (error) { setActionErr(error.message); return; }
    await loadDetail();
  }

  async function markReviewed(assignmentId: string) {
    setActionErr(null);
    const { error } = await supabase.rpc("canvas_assignment_mark_reviewed", { p_assignment_id: assignmentId });
    if (error) { setActionErr(error.message); return; }
    await loadDetail();
  }

  // Color the hail-size pill the same way the canvas overlay does
  const hailCls = storm.max_size_in >= 2 ? "bg-red-600" : storm.max_size_in >= 1.5 ? "bg-orange-500" : "bg-yellow-500";
  const expCls =
    storm.days_until_expire <= 30  ? "text-red-600 font-bold" :
    storm.days_until_expire <= 60  ? "text-orange-600 font-bold" :
    storm.days_until_expire <= 120 ? "text-amber-700" :
    "text-slate-500";

  // Pull zone-level KPIs from the detail (first row when row_kind='zone'), else fall back to the storm summary
  const zoneRow = detail?.find((r) => r.row_kind === "zone");
  const zoneKpis = zoneRow ?? null;

  const days_ago = 365 - storm.days_until_expire;

  return (
    <div className={`rounded-lg border bg-white shadow-sm ${
      storm.days_until_expire <= 60 && storm.bardin_visits === 0 ? "border-red-300" : "border-slate-200"
    }`}>
      {/* Card header — always visible */}
      <button
        onClick={onToggle}
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-slate-50"
      >
        <span className={`flex h-9 min-w-[3rem] items-center justify-center rounded-md px-2 text-sm font-bold text-white ${hailCls}`}>
          {Number(storm.max_size_in).toFixed(2)}″
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">
            {new Date(storm.storm_date + "T12:00:00Z").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
            <span className="ml-2 text-xs font-normal text-slate-500">
              · {days_ago}d ago · <span className={expCls}>{storm.days_until_expire <= 0 ? "expired" : `${storm.days_until_expire}d left`}</span>
              {storm.days_until_expire > 0 && storm.days_until_expire <= 30 ? " 🚨" : null}
            </span>
          </div>
          <div className="font-mono text-[11px] text-slate-400">
            {storm.signature_count} sigs · centroid {Number(storm.centroid_lat).toFixed(3)}, {Number(storm.centroid_lon).toFixed(3)}
          </div>
        </div>

        {/* Zone-level 6 KPIs (shown in header when expanded, abbreviated when collapsed) */}
        <div className="hidden flex-row items-center gap-3 sm:flex">
          {zoneKpis ? (
            <>
              <ZKpi label="Knocks"   value={zoneKpis.knocks} />
              <ZKpi label="Contacts" value={zoneKpis.contacts} />
              <ZKpi label="Apps"     value={zoneKpis.apps} />
              <ZKpi label="Claims"   value={zoneKpis.claims} />
              <ZKpi label="Builds"   value={zoneKpis.builds} />
              <ZKpi label="Revenue"  value={fmtMoney(Number(zoneKpis.revenue_cents))} />
            </>
          ) : (
            <>
              <ZKpi label="Visits"  value={storm.bardin_visits} />
              <ZKpi label="Appts"   value={storm.bardin_appts} />
              <ZKpi label="Tap"     value="↓" hint="for sub-areas" />
            </>
          )}
        </div>

        <span className={`ml-1 text-base text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}>
          ▶
        </span>
      </button>
      <div className="flex justify-end border-t border-slate-100 px-3 py-1.5">
        <a
          href={`/canvas?${new URLSearchParams({
            storm: storm.storm_date,
            lat: String(storm.centroid_lat),
            lon: String(storm.centroid_lon),
          }).toString()}`}
          target="_blank"
          rel="noopener"
          className="rounded-md bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-700 hover:bg-orange-100"
          title="Open this storm on the canvas map in a new tab"
        >
          🗺 Open on map ↗
        </a>
      </div>

      {/* Card body — sub-area drilldown */}
      {expanded ? (
        <div className="border-t border-slate-200">
          {loadingDetail ? (
            <div className="p-4 text-xs text-slate-500">Loading sub-areas…</div>
          ) : actionErr ? (
            <div className="m-3 rounded-md bg-red-50 p-2 text-xs text-red-800">⚠ {actionErr}</div>
          ) : null}

          {detail ? (
            <table className="w-full text-xs">
              <thead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="px-3 py-2 text-left">Area / Assigned to</th>
                  <th className="px-2 py-2 text-right">Pins</th>
                  <th className="px-2 py-2 text-right">Knocks</th>
                  <th className="px-2 py-2 text-right">Contacts</th>
                  <th className="px-2 py-2 text-right">Apps</th>
                  <th className="px-2 py-2 text-right">Claims</th>
                  <th className="px-2 py-2 text-right">Builds</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {detail.filter((r) => r.row_kind !== "zone").map((r, i) => {
                  const isUnassigned = r.row_kind === "unassigned";
                  const lastActivity = r.last_activity_at
                    ? `Last knock ${relativeTime(r.last_activity_at)}`
                    : "No activity yet";
                  // Stale flag: ≥7 days since the last activity / review / assignment date.
                  // Unassigned rows don't have an assignment to mark reviewed, so skip.
                  const idle = isUnassigned ? null : daysIdleFor(r);
                  const isStale = idle !== null && idle >= 7;
                  return (
                    <tr key={r.area_id ?? `unassigned-${i}`} className={`border-b border-slate-100 last:border-0 ${
                      isUnassigned ? "bg-slate-50/50" : "hover:bg-slate-50"
                    }`}>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                            isUnassigned ? "bg-slate-200 text-slate-500" : "bg-orange-100 text-orange-700"
                          }`}>
                            {isUnassigned ? "∅" : (r.assigned_to_name?.[0] ?? "?")}
                          </span>
                          <div className="leading-tight">
                            <div className="text-xs font-semibold text-slate-900">
                              {isUnassigned ? "Unassigned"
                                : r.area_label
                                  ? <>{r.assigned_to_name} <span className="font-normal text-slate-500">— {r.area_label}</span></>
                                  : r.assigned_to_name}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {isUnassigned
                                ? "Open to anyone"
                                : `Assigned ${relativeTime(r.assigned_at!)} · ${lastActivity}`}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-right font-bold tabular-nums text-slate-800">{r.pin_count}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{r.knocks}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{r.contacts}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{r.apps}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{r.claims}</td>
                      <td className="px-2 py-2.5 text-right tabular-nums text-slate-700">{r.builds}</td>
                      <td className="px-3 py-2.5 text-right">
                        {isUnassigned ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {isStale ? (
                              <span
                                title={`${idle} days since last visit, pin state change, or review`}
                                className="rounded-full bg-amber-100 px-2 py-[2px] text-[10px] font-medium text-amber-800"
                              >
                                Stale · {idle}d no activity
                              </span>
                            ) : null}
                            <button
                              onClick={() => setManageRow(r)}
                              className="rounded border border-slate-300 px-2 py-0.5 text-[10px] font-semibold text-slate-700 hover:bg-slate-100"
                            >
                              Manage
                            </button>
                            <button
                              onClick={() => removeArea(r.area_group_id!)}
                              className="rounded border border-red-300 px-2 py-0.5 text-[10px] font-semibold text-red-700 hover:bg-red-50"
                            >
                              Remove
                            </button>
                            <button
                              onClick={() => markReviewed(r.area_id!)}
                              className="rounded border border-emerald-300 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-50"
                              title="Stamp the area as reviewed — clears the stale flag for 7 days"
                            >
                              Mark reviewed
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {detail.filter((r) => r.row_kind !== "zone").length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-center text-xs text-slate-500">
                      No pins or assignments inside this zone yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          ) : null}

          {/* Manage Reps modal */}
          {manageRow ? (
            <ManageRepsModal
              row={manageRow}
              onClose={() => setManageRow(null)}
              onSaved={async () => { setManageRow(null); await loadDetail(); }}
            />
          ) : null}

          {/* Carrier breakdown */}
          <div className="border-t border-slate-100 bg-slate-50 px-3 py-2">
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold uppercase tracking-wider text-slate-500">
                🛡 Carrier breakdown
                <span className="ml-2 text-[10px] font-normal text-slate-400">
                  · {carriers.length} carrier{carriers.length === 1 ? "" : "s"}
                  {carriers[0]?.total_corrections ? ` · ${carriers[0].total_corrections} door-corrections` : ""}
                </span>
              </summary>
              <div className="mt-2">
                {carriers.length === 0 ? (
                  <div className="text-[11px] italic text-slate-500">
                    No canvas-sourced deals in this zone yet. As reps submit appointments and they get approved,
                    door-stated carrier (frozen at intake) and the corrected carrier will both populate here, with
                    claims-filed and claims-approved rates per carrier.
                  </div>
                ) : (
                  <table className="w-full text-[11px]">
                    <thead className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">
                      <tr className="border-b border-slate-200">
                        <th className="px-2 py-1.5 text-left">Carrier</th>
                        <th className="px-2 py-1.5 text-right">Deals</th>
                        <th className="px-2 py-1.5 text-right">Claims filed</th>
                        <th className="px-2 py-1.5 text-right">Approved</th>
                        <th className="px-2 py-1.5 text-right">Approval rate</th>
                        <th className="px-2 py-1.5 text-right">Built</th>
                        <th className="px-2 py-1.5 text-right">Door-corrections</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carriers.map((c) => {
                        const rate = c.claims_filed > 0
                          ? Math.round(100 * c.claims_approved / c.claims_filed)
                          : null;
                        const rateCls = rate === null ? "text-slate-400" :
                          rate >= 80 ? "text-emerald-700 font-bold" :
                          rate >= 50 ? "text-amber-700 font-bold" :
                          "text-red-700 font-bold";
                        return (
                          <tr key={c.carrier} className="border-b border-slate-100 last:border-0 hover:bg-white">
                            <td className="px-2 py-1.5 font-semibold text-slate-800">{c.carrier}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{c.total_deals}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{c.claims_filed}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{c.claims_approved}</td>
                            <td className={`px-2 py-1.5 text-right tabular-nums ${rateCls}`}>
                              {rate === null ? "—" : `${rate}%`}
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums text-slate-700">{c.builds_completed}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">
                              {c.corrected_to > 0 ? (
                                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800">
                                  ⚠ {c.corrected_to}
                                </span>
                              ) : (
                                <span className="text-slate-400">0</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div className="mt-2 text-[10px] text-slate-400">
                  Door-corrections = homeowner stated one carrier at the door, the actual carrier turned out
                  to be different (Ship 11b accuracy tracking via deals.insurance_company_initial).
                </div>
              </div>
            </details>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ZKpi({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="px-1.5 text-center">
      <div className="text-base font-bold tabular-nums text-slate-900">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      {hint ? <div className="text-[8px] text-slate-400">{hint}</div> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// ManageRepsModal — add or remove reps from an existing area_group_id without
// redrawing the boundary. Loads all active assignments for the group, lets the
// manager toggle reps on/off, then INSERTs new rows for added reps and UPDATEs
// status='removed' for unassigned ones — preserves the polygon + pin_ids.
// ─────────────────────────────────────────────────────────────────────────
function ManageRepsModal({
  row, onClose, onSaved,
}: { row: ZoneDetailRow; onClose: () => void; onSaved: () => void }) {
  const { member } = useAuth();
  // Track current rows for diffing add/remove (used to compose save payload)
  const [, setAreaRows] = useState<Array<{
    id: string; assigned_to: string; assigned_to_name: string;
  }>>([]);
  const [roster, setRoster] = useState<Array<{ id: string; full_name: string; role: string }>>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [originalSelected, setOriginalSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // 1. Pull all active rows for this area_group (one per rep)
      const { data: rows } = await supabase
        .from("canvas_area_assignments")
        .select("id, assigned_to, team_roster!canvas_area_assignments_assigned_to_fkey(full_name)")
        .eq("area_group_id", row.area_group_id)
        .eq("status", "active");
      // 2. Pull canvasser-tier roster for the picker
      const { data: rosterRows } = await supabase
        .from("team_roster")
        .select("id, full_name, role")
        .eq("is_active", true)
        .order("full_name");
      if (cancelled) return;
      const flat = (rows ?? []).map((r: any) => ({
        id: r.id, assigned_to: r.assigned_to,
        assigned_to_name: r.team_roster?.full_name ?? "—",
      }));
      setAreaRows(flat);
      const initial = new Set(flat.map((r) => r.assigned_to));
      setSelected(initial);
      setOriginalSelected(new Set(initial));
      setRoster((rosterRows ?? []) as any);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [row.area_group_id]);

  function toggle(repId: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(repId)) next.delete(repId);
      else next.add(repId);
      return next;
    });
  }

  async function save() {
    if (!member) return;
    setSaving(true);
    setErr(null);
    try {
      const adds = Array.from(selected).filter((id) => !originalSelected.has(id));
      const removes = Array.from(originalSelected).filter((id) => !selected.has(id));

      // Adds: clone the existing area's boundary + pin_ids, INSERT a new row per rep.
      if (adds.length > 0) {
        const { data: template } = await supabase
          .from("canvas_area_assignments")
          .select("boundary_geom, pin_ids, share_mode, area_label, notes, storm_signature_id")
          .eq("area_group_id", row.area_group_id)
          .eq("status", "active")
          .limit(1)
          .single();
        if (template) {
          const newRows = adds.map((rep_id) => ({
            area_group_id: row.area_group_id,
            assigned_to: rep_id,
            assigned_by: member.id,
            boundary_geom: template.boundary_geom,
            pin_ids: template.pin_ids,
            share_mode: template.share_mode,
            area_label: template.area_label,
            notes: template.notes,
            storm_signature_id: template.storm_signature_id,
          }));
          const { error } = await supabase.from("canvas_area_assignments").insert(newRows);
          if (error) throw error;
        }
      }

      // Removes: flip status='removed' on the rows for those reps.
      if (removes.length > 0) {
        const { error } = await supabase
          .from("canvas_area_assignments")
          .update({ status: "removed", removed_at: new Date().toISOString(), removed_by: member.id })
          .eq("area_group_id", row.area_group_id)
          .eq("status", "active")
          .in("assigned_to", removes);
        if (error) throw error;
      }

      onSaved();
    } catch (e: any) {
      setErr(e.message ?? String(e));
    } finally {
      setSaving(false);
    }
  }

  const totalSelected = selected.size;
  const dirty = adds(selected, originalSelected) > 0 || adds(originalSelected, selected) > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-2xl">
        <div className="border-b border-slate-200 px-4 py-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manage reps · area</div>
              <div className="text-sm font-semibold text-slate-900">
                {row.area_label ?? "(unlabeled area)"}
              </div>
              <div className="text-[11px] text-slate-500">{row.pin_count} pins · same boundary</div>
            </div>
            <button onClick={onClose} className="text-2xl leading-none text-slate-400 hover:text-slate-700">×</button>
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto p-4">
          {loading ? (
            <div className="text-sm text-slate-500">Loading reps…</div>
          ) : (
            <>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {totalSelected} rep{totalSelected === 1 ? "" : "s"} selected
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {roster
                  .filter((r) => ["canvasser","restore_manager","team_lead","asm","roof_specialist"].includes(r.role))
                  .map((r) => {
                    const sel = selected.has(r.id);
                    const wasOriginal = originalSelected.has(r.id);
                    return (
                      <button
                        key={r.id}
                        onClick={() => toggle(r.id)}
                        className={`rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                          sel
                            ? "border-orange-500 bg-orange-50 text-orange-900"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <div className="font-semibold">
                          {r.full_name}
                          {sel && !wasOriginal ? <span className="ml-1 text-[9px] text-emerald-600">+ NEW</span> : null}
                          {!sel && wasOriginal ? <span className="ml-1 text-[9px] text-red-600">– REMOVING</span> : null}
                        </div>
                        <div className="text-[10px] text-slate-500">{r.role}</div>
                      </button>
                    );
                  })}
              </div>
            </>
          )}
          {err ? <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-800">⚠ {err}</div> : null}
        </div>

        <div className="flex items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <button onClick={onClose} disabled={saving}
            className="rounded-md bg-white border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={save} disabled={saving || !dirty || totalSelected === 0}
            className="flex-1 rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50">
            {saving ? "Saving…" : totalSelected === 0 ? "At least 1 rep required" : `Save · ${totalSelected} rep${totalSelected === 1 ? "" : "s"} assigned`}
          </button>
        </div>
      </div>
    </div>
  );
}

function adds(a: Set<string>, b: Set<string>): number {
  let n = 0;
  for (const x of a) if (!b.has(x)) n++;
  return n;
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function Pill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-xs font-semibold ${
        active ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

function Kpi({
  label, value, hint, loading, accent,
}: {
  label: string; value: number | string; hint: string; loading: boolean;
  accent: "orange" | "purple" | "peach" | "cyan" | "green" | "teal";
}) {
  const cls =
    accent === "orange" ? "text-orange-600" :
    accent === "purple" ? "text-purple-600" :
    accent === "peach"  ? "text-orange-400" :
    accent === "cyan"   ? "text-cyan-600" :
    accent === "green"  ? "text-emerald-600" :
    "text-teal-700";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${cls}`}>
        {loading ? <span className="inline-block h-7 w-12 animate-pulse rounded bg-slate-100" /> : (typeof value === "number" ? value.toLocaleString() : value)}
      </div>
      <div className="mt-1 text-[10px] text-slate-500">{hint}</div>
    </div>
  );
}

function SummaryTile({
  label, value, hint, accent, urgent, active, onClick,
}: {
  label: string; value: number; hint: string;
  accent: "orange" | "red" | "teal"; urgent?: boolean;
  active?: boolean; onClick?: () => void;
}) {
  const valueCls =
    accent === "red"  ? (urgent ? "text-red-600" : "text-red-500") :
    accent === "teal" ? "text-teal-700" :
    "text-orange-600";
  const borderCls = active
    ? "border-orange-500 bg-orange-50 ring-2 ring-orange-200"
    : urgent
      ? "border-red-300 bg-red-50"
      : "border-slate-200 bg-slate-50";
  const hoverCls = onClick ? "cursor-pointer hover:border-slate-400 hover:bg-white" : "";
  const Tag: any = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className={`block w-full rounded-md border p-3 text-left transition-colors ${borderCls} ${hoverCls}`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${valueCls}`}>{value.toLocaleString()}</div>
      <div className="mt-0.5 text-[10px] text-slate-500">{hint}</div>
    </Tag>
  );
}

function fmtMoney(cents: number): string {
  if (!cents) return "$0";
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(0)}k`;
  return `$${dollars.toFixed(0)}`;
}
